<template>
  <ui-input
    v-model="insideValue"
    inputmode="decimal"
    :placeholder="placeholder"
    :disabled="disabled"
    @focus="$event => $emit('focus', $event)"
    @blur="$event => onBlur($event)"
    @keydown="$event => onKeyDown($event)"
    @keyup="$event => onKeyUp($event)"
    @paste="$event => onPaste($event)"
  />
</template>

<script>
/**
 * Text input that only takes numbers: `decimals` (or the decimals of `step`),
 * ArrowUp/ArrowDown step, `min`/`max` enforced on blur and while typing.
 */
export default {
  name: 'NumberInput',
  props: {
    modelValue: {
      type: [ Number, String, null ],
      required: false,
      default: () => null,
    },
    min: {
      type: [ Number, null ],
      required: false,
      default: () => null,
    },
    max: {
      type: [ Number, null ],
      required: false,
      default: () => null,
    },
    step: {
      type: Number,
      required: false,
      default: () => 1,
    },
    placeholder: {
      type: String,
      required: false,
      default: () => '',
    },
    decimals: {
      type: Number,
      required: false,
      default: () => 0,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  emits: [ 'update:modelValue', 'focus', 'blur', 'keydown', 'keyup' ],
  data: () => ({
    insideValue: '',
  }),
  computed: {
    numberValue() {
      const val = parseFloat(this.modelValue);
      return isNaN(val) ? 0 : val;
    },
    computedDecimals() {
      if (this.decimals <= 0) {
        return this.step.toFixed(3).split('.')[1].replace(/0+$/, '').length;
      }

      return this.decimals;
    },
  },
  watch: {
    insideValue(val) {
      if (isNaN(parseFloat(val))) {
        val = '';
      }
      else if (typeof val === 'string' && val.indexOf(',') !== -1) {
        val = val.replace(',', '.');
      }

      this.$emit('update:modelValue', val);
    },
    modelValue: {
      immediate: true,
      handler() {
        this.insideValue = this.modelValue;
      },
    },
  },
  methods: {
    focus() {
      this.$el.focus();
    },
    onKeyDown(event) {
      this.$nextTick(() => this.$emit('keydown', event));

      if (event.key === undefined) {
        return;
      }

      const keyVal = parseInt(event.key);
      const keyIsNumber = !isNaN(keyVal);

      if (keyIsNumber) {
        let newValue;

        const selectStart = event.target.selectionStart;
        const selectEnd = event.target.selectionEnd;
        if (selectStart !== selectEnd) {
          const beforeSelected = parseInt(this.insideValue.toString().substring(0, selectStart));
          const afterSelected = parseInt(this.insideValue.toString().substring(selectEnd, this.insideValue.length));

          newValue = '';
          if (!isNaN(beforeSelected)) {
            newValue += beforeSelected;
          }
          newValue += keyVal;
          if (!isNaN(afterSelected)) {
            newValue += afterSelected;
          }
          newValue = parseInt(newValue);
        }
        else {
          newValue = parseInt(this.insideValue + keyVal);
        }

        if (this.max !== null && newValue > this.max) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (event.isPaste) {
          this.insideValue = newValue;
          this.$emit('update:modelValue', this.insideValue);
        }
      }

      if (event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) {
        return;
      }

      if (event.key === 'ArrowUp') {
        let newValue = this.numberValue + this.step;
        if (this.max !== null && newValue > this.max) {
          newValue = this.max;
        }
        newValue = Number(newValue.toFixed(4));
        this.$emit('update:modelValue', newValue);
        return;
      }
      else if (event.key === 'ArrowDown') {
        let newValue = this.numberValue - this.step;
        newValue = Number(newValue.toFixed(4));
        if (this.min !== null && newValue < this.min) {
          newValue = this.min;
        }
        this.$emit('update:modelValue', newValue);
        return;
      }

      if (
        (event.key === '.' || event.key === ',')
        && (this.computedDecimals === 0 || this.insideValue.toString().indexOf('.') !== -1)
      ) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (
        event.key.length === 1
        && event.key !== '.'
        && event.key !== ','
        && !keyIsNumber
      ) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    },
    onKeyUp($event) {
      this.$emit('keyup', $event);

      const [ number, decimal ] = this.insideValue.toString().split('.');
      if (parseInt(this.computedDecimals) === 0) {
        this.insideValue = number;
      }
      else if (decimal && decimal.length > this.computedDecimals) {
        const trimmedDecimal = decimal.substring(0, this.computedDecimals);
        this.insideValue = number + '.' + trimmedDecimal;
      }
    },
    onBlur($event) {
      if (this.min !== null && this.numberValue < this.min) {
        this.$emit('update:modelValue', this.min);
      }
      else if (this.max !== null && this.numberValue > this.max) {
        this.$emit('update:modelValue', this.max);
      }

      this.$emit('blur', $event);
    },
    async onPaste(event) {
      event.preventDefault();

      const value = await navigator.clipboard.readText();

      this.onKeyDown({
        isPaste: true,
        key: value,
        target: event.target,
        preventDefault() {
          return;
        },
        stopPropagation() {
          return;
        },
      });
    },
  },
};
</script>
